import { useMemo } from 'react';
import type { TicketProgress } from '@shared/schemas';
import { ticketAssignees } from '@/lib/ticket-meta';

/**
 * Q-5 multi-assignee filter dropdown · Board.tsx peer component.
 *
 * Replaces single «АК» badge convention with dropdown filter chip. Per
 * CHAT_2B_PENDING_QUESTIONS.md Q-5 (Genie adopt 16 MAY): «Powers АК/МС/ДС
 * filter UI · was dropped в C 16 MAY · add back когда field available».
 *
 * Schema `progress.tickets[id].assignees: string[]` shipped @ 8c208a5 ·
 * helper `filterTicketsByAssignee` shipped @ cfa3c1a · this component is the
 * UI consumer. Board.tsx imports + renders above ticket list · onChange
 * filters visible tickets.
 *
 * Behavior:
 *   - Derives unique assignee ids from `tickets` map (sorted alphabetically)
 *   - Renders chip-style buttons: «Все» (no filter) · per-assignee · «Никто» (unassigned)
 *   - Click toggles active filter · second click on same chip clears к null
 *   - Active state visually distinct (filled vs outline)
 *   - Empty assignee pool → renders nothing (defensive · no filter affordance needed)
 */

export interface AssigneeFilterProps {
  /** All ticket progress in scope (e.g. current sprint). Component derives assignee set. */
  tickets: Record<string, Pick<TicketProgress, 'assignees'>>;
  /** Currently selected assignee id · `null` = unassigned filter · `undefined` = no filter («Все»). */
  selected: string | null | undefined;
  /** Called on chip click. Pass `undefined` к clear filter · `null` для unassigned · string id otherwise. */
  onChange: (next: string | null | undefined) => void;
  /** Optional label override per locale · defaults to «Исполнитель». */
  label?: string;
}

export function AssigneeFilter({
  tickets,
  selected,
  onChange,
  label = 'Исполнитель',
}: AssigneeFilterProps) {
  // Derive unique assignee pool · sorted · stable across renders
  const assignees = useMemo(() => {
    const set = new Set<string>();
    for (const t of Object.values(tickets)) {
      for (const id of ticketAssignees(t)) {
        set.add(id);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  // Don't render filter affordance if no assignees exist (would be all «Никто»)
  if (assignees.length === 0) return null;

  const handleClick = (target: string | null | undefined) => {
    // Second click on active chip clears filter
    if (selected === target) {
      onChange(undefined);
    } else {
      onChange(target);
    }
  };

  return (
    <div
      role="group"
      aria-label={label}
      className="assignee-filter"
      style={{
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 'var(--font-xs, 12px)',
          color: 'var(--ink-muted)',
          marginRight: 4,
        }}
      >
        {label}:
      </span>

      <Chip
        active={selected === undefined}
        label="Все"
        onClick={() => handleClick(undefined)}
      />

      {assignees.map((id) => (
        <Chip
          key={id}
          active={selected === id}
          label={id}
          onClick={() => handleClick(id)}
        />
      ))}

      <Chip
        active={selected === null}
        label="Никто"
        onClick={() => handleClick(null)}
      />
    </div>
  );
}

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`assignee-chip${active ? ' assignee-chip-active' : ''}`}
      style={{
        padding: '2px 8px',
        fontSize: 'var(--font-xs, 12px)',
        borderRadius: 12,
        border: '1px solid var(--border-strong)',
        background: active ? 'var(--ink)' : 'var(--surface-2)',
        color: active ? 'var(--surface)' : 'var(--ink)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
